import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { Warning } from "@material-ui/icons";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";
import { getContractByProfileId, getWarnings } from "../../../reducer/raContract/action";

// [VinUni] Module 6 - Hiển thị hợp đồng TLNC của cá nhân (quản lý KPI) trong trang cá nhân

const PRODUCT_TYPE_LABEL = {
  scientificResearch: "Bài báo",
  monograph: "Sách/Giáo trình",
  solution: "Giải pháp hữu ích",
  patent: "Bằng sáng chế",
  researchProposal: "Đề tài",
  other: "Khác",
};

const STATUS_LABEL = {
  draft: "Nháp",
  active: "Đang hiệu lực",
  completed: "Đã nghiệm thu",
  expired: "Hết hạn",
  cancelled: "Đã huỷ",
};

const formatDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("vi-VN");
};

function RaContractProfile(props) {
  const { profile, contracts, warnings, isFetching } = props;
  const profileId = profile?.id;

  useEffect(() => {
    if (profileId) {
      props.getContractByProfileId(profileId);
      props.getWarnings(profileId);
    }
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const myWarnings = useMemo(() => warnings || [], [warnings]);

  const progressOf = (contract) => {
    const required = (contract.deliverables || []).reduce((s, d) => s + (d.requiredQuantity || 0), 0);
    const done = (contract.acceptances || []).length;
    return { done, required };
  };

  return (
    <Box>
      {myWarnings.length > 0 && (
        <Card style={{ padding: 12, marginBottom: 12, background: "#fff4e5", border: "1px solid #ffb74d" }}>
          <Box display="flex" alignItems="center" marginBottom={1}>
            <Warning style={{ color: "#e65100", marginRight: 8 }} />
            <Typography style={{ fontWeight: "bold", color: "#e65100" }}>Cảnh báo hợp đồng</Typography>
          </Box>
          {myWarnings.map((w, i) => (
            <Typography key={i} variant="body2" style={{ color: "#7a4f01" }}>
              • Hợp đồng <b>{w.contract?.code || w.contract?.title || `#${w.contract?.id}`}</b>{" "}
              {w.warningType === "expiring" ? "sắp hết hạn" : "sắp đến kỳ nghiệm thu"} (còn {w.daysLeft} ngày)
            </Typography>
          ))}
        </Card>
      )}

      {isFetching ? (
        <Box display="flex" justifyContent="center" padding={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (contracts || []).length === 0 ? (
        <Typography style={{ padding: 16 }}>Bạn chưa có hợp đồng TLNC nào.</Typography>
      ) : (
        (contracts || []).map((c) => {
          const { done, required } = progressOf(c);
          return (
            <Card key={c.id} style={{ padding: 12, marginBottom: 12 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                <Typography variant="h6">
                  {c.code ? `${c.code} - ` : ""}
                  {c.title}
                </Typography>
                <Chip
                  size="small"
                  label={`${STATUS_LABEL[c.status] || c.status || "—"}${c.completed ? " ✓" : ""}`}
                  color={c.completed ? "primary" : "default"}
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Thời hạn: {formatDate(c.startDate)} → {formatDate(c.endDate)} | Hạn nghiệm thu:{" "}
                {formatDate(c.acceptanceDueDate)} | Tiến độ nghiệm thu: <b>{done}/{required}</b>
              </Typography>

              <Table size="small" style={{ marginTop: 8 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Sản phẩm cần nghiệm thu</TableCell>
                    <TableCell align="center">Yêu cầu</TableCell>
                    <TableCell align="center">Đã nghiệm thu</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(c.deliverables || []).map((d) => {
                    const doneOfType = (c.acceptances || []).filter((a) => a.productType === d.productType).length;
                    return (
                      <TableRow key={d.id}>
                        <TableCell>
                          {PRODUCT_TYPE_LABEL[d.productType] || d.productType}
                          {d.description ? ` (${d.description})` : ""}
                        </TableCell>
                        <TableCell align="center">{d.requiredQuantity}</TableCell>
                        <TableCell align="center" style={{ color: doneOfType >= d.requiredQuantity ? "green" : "inherit" }}>
                          {doneOfType}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          );
        })
      )}
    </Box>
  );
}

const mapStateToProps = (state) => ({
  profile: select(state, "profileReducer", "detail"),
  contracts: select(state, "raContractReducer", "listByProfile"),
  warnings: select(state, "raContractReducer", "warnings"),
  isFetching: select(state, "raContractReducer", "isFetching"),
});

const mapDispatchToProps = (dispatch) => ({
  getContractByProfileId: (id) => dispatch(getContractByProfileId(id)),
  getWarnings: (id) => dispatch(getWarnings(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(RaContractProfile));
